import React, { ReactNode } from 'react';
import { Badge as KitBadge, type BadgeStatus } from '@/design-system/badge';
import { cn } from '@/design-system/lib/utils';

export type BadgeTone =
  | 'primary'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'neutral'
  | 'amber'
  | 'orange'
  | 'sky'
  | 'rose'
  | 'emerald';

export type BadgeSize = 'small' | 'medium' | 'large';

export interface BadgeProps {
  /** Color tone — sets text + background + border. */
  tone?: BadgeTone;
  /** Show a leading colored dot. */
  dot?: boolean;
  /**
   * When true and `dot` is on, the dot pulses (for "live" / urgent states).
   */
  pulse?: boolean;
  size?: BadgeSize;
  /** Use tabular numerals (good for "3 days" style badges). */
  tabular?: boolean;
  className?: string;
  children: ReactNode;
}

/** App tone → kit status. Broad tone palette collapses onto the kit's
 * semantic state scale so domain badges stay on tokens. */
const TONE_TO_STATUS: Record<BadgeTone, BadgeStatus> = {
  primary: 'feature',
  success: 'success',
  warning: 'away',
  danger: 'error',
  info: 'information',
  neutral: 'neutral',
  amber: 'away',
  orange: 'warning',
  sky: 'verified',
  rose: 'highlighted',
  emerald: 'success',
};

/** Dark-tone dot fill per kit status — matches the "lighter" appearance
 * label color, used for the custom pulsing dot. */
const PULSE_DOT_BG: Record<BadgeStatus, string> = {
  error: 'bg-(--state-error-dark)',
  warning: 'bg-(--state-warning-dark)',
  away: 'bg-(--state-away-dark)',
  success: 'bg-(--state-success-dark)',
  information: 'bg-(--state-information-dark)',
  feature: 'bg-(--state-feature-dark)',
  verified: 'bg-(--state-verified-dark)',
  highlighted: 'bg-(--state-highlighted-dark)',
  stable: 'bg-(--state-stable-dark)',
  faded: 'bg-(--state-faded-dark)',
  neutral: 'bg-text-strong-950',
};

/**
 * Editorial pill — kit Badge (lighter appearance) with optional leading dot.
 * Use for status, aging, and inline meta tags.
 */
const Badge: React.FC<BadgeProps> = ({
  tone = 'neutral',
  dot = false,
  pulse = false,
  size = 'medium',
  tabular = false,
  className = '',
  children,
}) => {
  const status = TONE_TO_STATUS[tone];
  const kitSize = size === 'large' ? 'md' : 'sm';
  const pulsingDot = dot && pulse;

  return (
    <KitBadge
      status={status}
      appearance="lighter"
      size={kitSize}
      type={dot && !pulse ? 'dot' : 'default'}
      className={cn(
        pulsingDot && 'gap-1',
        tabular && 'tabular-nums',
        className
      )}
    >
      {pulsingDot && (
        <span
          aria-hidden
          className="relative inline-flex size-1.5 shrink-0"
        >
          <span
            className={cn(
              'absolute inline-flex h-full w-full animate-ping rounded-full opacity-75 motion-reduce:animate-none',
              PULSE_DOT_BG[status]
            )}
          />
          <span
            className={cn(
              'relative inline-flex h-full w-full rounded-full',
              PULSE_DOT_BG[status]
            )}
          />
        </span>
      )}
      {children}
    </KitBadge>
  );
};

export default Badge;
